import React, { useState, useRef, useEffect } from 'react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';

const FilterDropdown = ({
    value,
    onChange,
    options = [],
    placeholder,
    className = "w-40",
    up = false,
    disabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Find the currently selected option object
    const selectedOption = options.find(opt => String(opt.value) === String(value)) || { name: placeholder, value: '' };

    // Check if options array already contains an explicit default/reset option matching value '' or placeholder
    const hasDefaultOption = options.some(opt => opt.value === '' || (placeholder && opt.name && opt.name.toLowerCase() === placeholder.toLowerCase()));

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    const handleSelect = (val) => {
        onChange(val);
        setIsOpen(false);
    };

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            <div className="relative mt-1">
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    className={`relative w-full cursor-pointer rounded-xl bg-white border-2 border-slate-200 py-2.5 pl-4 pr-10 text-left shadow-sm focus:outline-none focus:border-indigo-500 sm:text-sm transition-all group ${
                        disabled ? 'opacity-50 pointer-events-none bg-slate-50' : 'hover:border-[#18216D]/30 hover:bg-slate-50'
                    }`}
                >
                    <span className={`block truncate font-black text-[10px] uppercase tracking-wider ${value ? 'text-[#18216D]' : 'text-slate-400'}`}>
                        {selectedOption.name}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronDownIcon
                            className={`h-5 w-5 text-slate-400 group-hover:text-[#18216D] transition-transform duration-200 ${
                                isOpen ? 'rotate-180 text-[#18216D]' : ''
                            }`}
                            aria-hidden="true"
                        />
                    </span>
                </button>

                {isOpen && (
                    <div
                        className={`absolute z-[100] ${
                            up ? 'bottom-full mb-1' : 'mt-1'
                        } max-h-60 w-full overflow-auto rounded-xl bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm animate-fadeIn`}
                        role="listbox"
                    >
                        {!hasDefaultOption && placeholder && (
                            <div
                                role="option"
                                aria-selected={!value}
                                onClick={() => handleSelect('')}
                                className={`relative cursor-pointer select-none py-2 pl-10 pr-4 transition-colors hover:bg-indigo-50 hover:text-indigo-900 ${
                                    !value ? 'bg-indigo-50/50 text-[#18216D]' : 'text-slate-500'
                                }`}
                            >
                                <span className={`block truncate font-bold text-[10px] uppercase tracking-wider ${!value ? 'font-black' : 'font-bold'}`}>
                                    {placeholder}
                                </span>
                                {!value ? (
                                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-[#FFD700]">
                                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                    </span>
                                ) : null}
                            </div>
                        )}
                        {options.map((option, idx) => {
                            const isSelected = String(option.value) === String(value);
                            return (
                                <div
                                    key={`opt-${option.value}-${idx}`}
                                    role="option"
                                    aria-selected={isSelected}
                                    onClick={() => handleSelect(option.value)}
                                    className={`relative cursor-pointer select-none py-2 pl-10 pr-4 transition-colors hover:bg-indigo-50 hover:text-indigo-900 ${
                                        isSelected ? 'bg-indigo-50/50 text-[#18216D]' : 'text-slate-900'
                                    }`}
                                >
                                    <span className={`block truncate font-bold text-[10px] uppercase tracking-wider ${isSelected ? 'font-black' : 'font-bold'}`}>
                                        {option.name}
                                    </span>
                                    {isSelected ? (
                                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-[#18216D]">
                                            <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                        </span>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FilterDropdown;
